<script setup>

import {ref} from 'vue'
import _ from "lodash";

const props = defineProps({

    columnNames: {
        type: Array,
        required: true
    },
    sampleData: {
        type: Object,
        required: true
    },
    importer: {
        type: Object,
    },
    availableLocales: {
        type: Array,
        required: true,
        default: () => [
            { code: 'pl', name: 'Polski', native: 'Polski', flag: '🇵🇱' },
            { code: 'en', name: 'English', native: 'English', flag: '🇬🇧' }
        ]
    },
    defaultLocale: {
        type: String,
        required: false,
        default: 'pl'
    }
});
const vats = [{
    value: 0,
    label: '0%'
}, {
    value: -8,
    label: '8% z brutto'
}, {
    value: 8,
    label: '8% z netto'
}, {
    value: -23,
    label: '23% z brutto'
}, {
    value: 23,
    label: '23% z netto'
},];
const actionNames = [
    {
        value: 'add',
        label: 'balance_importer.action_add'
    },
    {
        value: 'subtract',
        label: 'balance_importer.action_subtract'
    },
    {
        value: 'multiply',
        label: 'balance_importer.action_multiply'
    },
    {
        value: 'divide',
        label: 'balance_importer.action_divide'
    },
];

const importer = ref(props.importer ? props.importer : {
    cash: [],
    online: [],
    tip: [],
    provision: [],
    externalId: [],
    additionalData: [],
    vats: [],
    filter: {
        active: false,
        field: '',
        condition: '',
        value: ''
    }
});

if (importer.value.filter === undefined) {
    _.set(importer.value, 'filter', {
        active: false,
        field: '',
        condition: '',
        value: ''
    });
}

if (importer.value.provision === undefined) {
    _.set(importer.value, 'provision', []);
}

// Konwersja starych danych vats.name ze string na obiekt wielojęzyczny
if (importer.value.vats && Array.isArray(importer.value.vats)) {
    importer.value.vats.forEach(vat => {
        if (typeof vat.name === 'string') {
            // Stary format - konwertuj string na obiekt
            const oldName = vat.name;
            const nameObject = {};
            props.availableLocales.forEach(locale => {
                // Ustawiamy starą wartość dla pierwszego języka, resztę pusta
                nameObject[locale.code] = locale.code === props.availableLocales[0].code ? oldName : '';
            });
            vat.name = nameObject;
        } else if (!vat.name || typeof vat.name !== 'object') {
            // Brak name lub nieprawidłowy format - inicjalizuj pusty obiekt
            const nameObject = {};
            props.availableLocales.forEach(locale => {
                nameObject[locale.code] = '';
            });
            vat.name = nameObject;
        }
    });
}

const baseGroups = {
    cash: 'balance_importer.cash',
    online: 'balance_importer.online',
    provision: 'balance_importer.provision',
    tip: 'balance_importer.tip'
};

// Odpowiednik PHP-owego is_numeric(): parseFloat jest zbyt łagodny i z '12abc'
// zrobiłby 12, podczas gdy import policzy 0. sampleData przychodzi już
// z przecinkami zamienionymi na kropki (ApplicationController::balanceImporter()),
// więc podgląd i import czytają dokładnie te same wartości.
function isNumeric(value) {
    if (typeof value === 'number') {
        return Number.isFinite(value);
    }
    if (typeof value !== 'string') {
        return false;
    }

    // Białe znaki wypisane wprost, bo PHP-owe is_numeric() akceptuje wyłącznie
    // ASCII (spacja, \t, \n, \r, \v, \f). JS-owe \s i trim() obejmują też
    // twardą spację i spację cienką, którymi Excel rozdziela tysiące — i wtedy
    // podgląd czytałby liczbę tam, gdzie import widzi 0.
    return /^[ \t\n\r\v\f]*[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?[ \t\n\r\v\f]*$/.test(value);
}

// Tylko do wyświetlenia próbki w tabeli — ścieżka liczbowa idzie przez
// getOperandValue() i nie może korzystać z tej funkcji.
function getSampleValue(importerItem) {
    const rawValue = props.sampleData[importerItem.columnName];

    if (importerItem.isAbs && isNumeric(rawValue)) {
        return Math.abs(parseFloat(rawValue));
    }

    return rawValue;
}

// Wartość operandu: kolumna bierze ją z przykładowych danych, stała
// z konfiguracji. Odpowiednik resolveOperandValue() w BaseApplication.php,
// łącznie z obsługą isAbs dla obu rodzajów operandu — ptaszek „wartość
// bezwzględna" pokazujemy tylko przy kolumnach, ale flaga z ręcznie
// zredagowanego configu musi liczyć się tak samo po obu stronach.
function getOperandValue(importerItem) {
    const rawValue = importerItem.type === 'constant'
        ? importerItem.value
        : props.sampleData[importerItem.columnName];
    const value = isNumeric(rawValue) ? parseFloat(rawValue) : 0;

    return importerItem.isAbs ? Math.abs(value) : value;
}

// type="number" gubi wartość z przecinkiem (przeglądarka zwraca pusty string),
// dlatego pole stałej jest tekstowe, a przecinek zamieniamy przy wpisywaniu.
// Wartość zostaje stringiem w trakcie pisania (żeby nie ucinać wpisywanego
// '1.'), a na liczbę przechodzi dopiero przy serializacji w getImporterJson().
function setConstantValue(importerItem, rawValue) {
    importerItem.value = String(rawValue).replace(/,/g, '.');
}

function removeItem(group, index) {
    let item = importer.value[group];
    if (index !== undefined) {
        item = importer.value[group][index].items;
    }
    item.pop();
}

function addItem(group, type, index) {
    let item = importer.value[group];
    if (index !== undefined) {
        item = importer.value[group][index].items;
    }

    if (type === 'column') {
        item.push({
            type: 'column',
            columnName: '',
            isAbs: false,
        });
    } else if (type === 'constant') {
        item.push({
            type: 'constant',
            value: 0,
        });
    } else {
        item.push({
            type: 'action',
            actionName: ''
        });
    }
}

function addVat() {
    // Dynamicznie tworzymy obiekt name dla wszystkich dostępnych języków
    const nameObject = {};
    props.availableLocales.forEach(locale => {
        nameObject[locale.code] = '';
    });

    importer.value.vats.push({
        vat: 0,
        name: nameObject,
        items: []
    });
}

function getImporterJson() {
    const config = _.cloneDeep(importer.value);
    const numberifyConstants = (items) => {
        (items ?? []).forEach(item => {
            if (item.type === 'constant' && isNumeric(item.value)) {
                item.value = parseFloat(item.value);
            }
        });
    };

    Object.keys(baseGroups).forEach(group => numberifyConstants(config[group]));
    (config.vats ?? []).forEach(vat => numberifyConstants(vat.items));

    return JSON.stringify(config);
}

function groupItems(group, index) {
    if (index !== undefined) {
        return importer.value[group][index].items;
    }
    return importer.value[group];
}

// Jeden przebieg liczenia dla podglądu: zwraca wynik oraz informację, czy po
// drodze doszło do dzielenia przez zero. PHP w tym samym miejscu zapisuje
// ostrzeżenie do logu (BaseApplication::logDivisionByZero()), konfigurator
// pokazuje je obok wyniku.
function evaluateItems(itemToProcess) {
    let actionName = '';
    // null, a nie 0 — dokładnie jak $result w processField(). Przy dwóch
    // operandach obok siebie (bez działania między nimi) PHP zachowuje
    // PIERWSZY z nich, nie ostatni.
    let result = null;
    let divisionByZero = false;
    let unknownAction = false;

    for (let i = 0; i < itemToProcess.length; i++) {
        const item = itemToProcess[i];
        if (item.type === 'action') {
            actionName = item.actionName;
            continue;
        }
        const readValue = getOperandValue(item);
        if (actionName !== '') {
            if (actionName === 'divide' && readValue === 0) {
                divisionByZero = true;
            }
            if (!actionNames.some(action => action.value === actionName)) {
                unknownAction = true;
            }
            result = applyAction(result, readValue, actionName);
            actionName = '';
        } else {
            result = result ?? readValue;
        }
    }

    // Wynik NIEZAOKRĄGLONY — processVats() w PHP mnoży surową podstawę
    // i zaokrągla dopiero na końcu. Zaokrąglanie tutaj rozjeżdżałoby VAT-y
    // o grosz (np. 2/13 przy 23%: import 0.04, podgląd 0.03).
    return {result: result ?? 0, divisionByZero: divisionByZero, unknownAction: unknownAction};
}

function evaluateResult(group, index) {

    const itemToProcess = groupItems(group, index);
    if (!itemToProcess) {
        return '';
    }

    return _.round(evaluateItems(itemToProcess).result, 2);
}

function hasDivisionByZero(group, index) {

    const itemToProcess = groupItems(group, index);
    if (!itemToProcess) {
        return false;
    }

    return evaluateItems(itemToProcess).divisionByZero;
}

function hasUnknownAction(group, index) {

    const itemToProcess = groupItems(group, index);
    if (!itemToProcess) {
        return false;
    }

    return evaluateItems(itemToProcess).unknownAction;
}

// Musi dawać ten sam wynik co applyAction() w BaseApplication.php —
// podgląd w konfiguratorze nie może się rozjeżdżać z importem, łącznie
// z regułą „dzielnik 0 daje 0".
function applyAction(result, value, actionName) {
    switch (actionName) {
        case 'add':
            return result + value;
        case 'subtract':
            return result - value;
        case 'multiply':
            return result * value;
        case 'divide':
            return value === 0 ? 0 : result / value;
        default:
            return result;
    }
}

function evaluateVat(index) {
    const item = importer.value['vats'][index];
    const itemToProcess = groupItems('vats', index);
    if (!itemToProcess) {
        return 0;
    }

    let result = evaluateItems(itemToProcess).result;

    // Number(), bo switch w JS porównuje ŚCIŚLE, a PHP-owy switch w processVats()
    // luźno. Stawka zapisana jako string ('23') nałożyłaby w imporcie mnożnik,
    // a w podglądzie nie — i podgląd pokazałby surową podstawę jako kwotę VAT.
    switch (Number(item.vat)) {
        case -23:
            result = result * 0.187;
            break;
        case -8:
            result = result * 0.08 / 1.08;
            break;
        case 8:
            result = result * 0.08;
            break;
        case 23:
            result = result * 0.23;
            break;
    }

    // abs PRZED round, tak jak round(abs($result), 2) w processVats() —
    // dla wartości typu -0.125 kolejność zmienia wynik o grosz.
    return _.round(Math.abs(result), 2);
}

function evaluateString(group, separator) {
    const importerData = importer.value[group];
    if (importerData.length === 0) {
        return '';
    }
    let result = props.sampleData[importerData[0].columnName];
    if (importerData.length === 1) {
        return result;
    }
    for (let i = 1; i < importerData.length; i++) {
        const item = importerData[i];
        if (item.type === 'column' && props.sampleData[item.columnName]) {
            result += separator + props.sampleData[item.columnName];
        }
    }
    return result;
}

function canShowOperandButton(group, index) {

    let groupItems = importer.value[group];
    if (index !== undefined) {
        groupItems = importer.value[group][index].items;
    }

    return groupItems.length === 0 || groupItems[groupItems.length - 1].type === 'action';
}

function canShowActionButton(group, index) {

    let groupItems = importer.value[group];
    if (index !== undefined) {
        groupItems = importer.value[group][index].items;
    }

    return groupItems.length > 0 && groupItems[groupItems.length - 1].type !== 'action';
}

function getOptionLabel(option) {

    let foundIndex;
    // find index of option in columnNames
    props.columnNames.forEach((element, index) => {
        if (element === option) {
            foundIndex = index;

        }
    });
    if (foundIndex) {
        return `${foundIndex}: ${option}`;
    }
    return option;
}
</script>

<template>
    <div v-for="(groupName, groupKey) in baseGroups" class="mb-4">
        <h3>{{ $t(groupName) }}</h3>
        <table class="table table-striped">
            <thead>
            <tr>
                <th class="w-50">{{ $t('balance_importer.process') }}</th>
                <th class="w-25">{{ $t('balance_importer.other') }}</th>
                <th class="w-25">{{ $t('balance_importer.sample_data') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="importerItem in importer[groupKey]">
                <td>
                    <div v-if="importerItem.type==='column'">
                        <v-select :clearable="false"
                                  :getOptionLabel="getOptionLabel"
                                  v-model="importerItem.columnName"
                                  :options="columnNames">

                        </v-select>
                        <span v-if="!importerItem.columnName" class="d-block text-warning">
                            {{ $t('balance_importer.column_not_selected') }}
                        </span>
                    </div>
                    <div v-if="importerItem.type==='constant'">
                        <input type="text"
                               inputmode="decimal"
                               class="form-control"
                               :value="importerItem.value"
                               @input="setConstantValue(importerItem, $event.target.value)"
                               :placeholder="$t('balance_importer.constant_value')">
                    </div>
                    <div v-if="importerItem.type==='action'">
                        <v-select :clearable="false"
                                  :reduce="item => item.value"
                                  :searchable="false"
                                  :getOptionLabel="(item) => $t(item.label)"
                                  v-model="importerItem.actionName"
                                  :options="actionNames">
                        </v-select>
                    </div>
                </td>
                <td>
                    <div v-if="importerItem.type==='column'">
                        <label>
                            <input type="checkbox" v-model="importerItem.isAbs">
                            Wartość bezwzględna
                        </label>
                    </div>
                </td>
                <td>
                    <div v-if="importerItem.type==='column'">
                        {{ getSampleValue(importerItem) }}

                    </div>
                    <div v-if="importerItem.type==='constant'">
                        {{ getOperandValue(importerItem) }}
                    </div>
                    <div v-if="importerItem.type==='action'">
                        {{ $t(actionNames.find(action => action.value === importerItem.actionName)?.label ?? '') }}
                    </div>
                </td>
            </tr>
            </tbody>
            <tfoot>
            <tr>
                <td></td>
                <td></td>
                <td>
                    {{ evaluateResult(groupKey) }}
                    <span v-if="hasDivisionByZero(groupKey)" class="d-block text-warning">
                        {{ $t('balance_importer.division_by_zero') }}
                    </span>
                    <span v-if="hasUnknownAction(groupKey)" class="d-block text-warning">
                        {{ $t('balance_importer.unknown_action') }}
                    </span>
                </td>
            </tr>
            </tfoot>
        </table>
        <button v-if="canShowOperandButton(groupKey)" type="button" class="btn btn-primary"
                @click="addItem(groupKey,'column')">
            {{ $t('balance_importer.add_column') }}
        </button>
        <button v-if="canShowOperandButton(groupKey)" type="button" class="btn btn-primary"
                @click="addItem(groupKey,'constant')">
            {{ $t('balance_importer.add_constant') }}
        </button>
        <button v-if="canShowActionButton(groupKey)" type="button" class="btn btn-primary"
                @click="addItem(groupKey,'action')">
            {{ $t('balance_importer.add_action') }}
        </button>
        <button type="button" class="btn btn-outline-danger"
                v-if="importer[groupKey].length>0"
                @click="removeItem(groupKey)">
            {{ $t('balance_importer.remove_last_record') }}
        </button>
    </div>
    <h2>{{ $t('balance_importer.vats') }}</h2>
    <div v-for="(vat, index) in importer.vats" class="mb-4">
        <div class="form-group">
            <label>{{ $t('balance_importer.name') }}</label>
            <!-- Zakładki językowe -->
            <ul class="nav nav-tabs" :id="'vat-name-tabs-' + index" role="tablist">
                <li v-for="(locale, localeIndex) in availableLocales" :key="locale.code" class="nav-item" role="presentation">
                    <a :class="[
                           'nav-link',
                           localeIndex === 0 ? 'active' : '',
                           locale.code === defaultLocale ? 'font-weight-bold' : ''
                       ]"
                       :id="'vat-name-' + index + '-' + locale.code + '-tab'"
                       data-toggle="tab"
                       :href="'#vat-name-' + index + '-' + locale.code"
                       role="tab"
                       :aria-controls="'vat-name-' + index + '-' + locale.code"
                       :aria-selected="localeIndex === 0 ? 'true' : 'false'">
                        {{ locale.native }}
                        <span v-if="locale.code === defaultLocale" class="badge badge-primary">domyślny</span>
                    </a>
                </li>
            </ul>
            <!-- Zawartość zakładek -->
            <div class="tab-content border border-top-0 p-3" :id="'vat-name-tabs-content-' + index">
                <div v-for="(locale, localeIndex) in availableLocales"
                     :key="locale.code"
                     :class="['tab-pane', 'fade', localeIndex === 0 ? 'show active' : '']"
                     :id="'vat-name-' + index + '-' + locale.code"
                     role="tabpanel"
                     :aria-labelledby="'vat-name-' + index + '-' + locale.code + '-tab'">
                    <input type="text"
                           v-model="vat.name[locale.code]"
                           class="form-control"
                           :placeholder="$t('balance_importer.name') + ' (' + locale.native + ')'">
                </div>
            </div>
        </div>
        <p>{{ $t('balance_importer.rate') }}
            <v-select :clearable="false"
                      v-model="vat.vat"
                      :reduce="item => item.value"
                      :options="vats"></v-select>
        </p>
        <table class="table table-striped">
            <thead>
            <tr>
                <th class="w-50">{{ $t('balance_importer.process') }}</th>
                <th class="w-25">{{ $t('balance_importer.other') }}</th>
                <th class="w-25">{{ $t('balance_importer.sample_data') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="importerItem in vat.items">
                <td>
                    <div v-if="importerItem.type==='column'">
                        <v-select :clearable="false"

                                  :getOptionLabel="getOptionLabel"
                                  v-model="importerItem.columnName"
                                  :options="columnNames">

                        </v-select>
                        <span v-if="!importerItem.columnName" class="d-block text-warning">
                            {{ $t('balance_importer.column_not_selected') }}
                        </span>
                    </div>
                    <div v-if="importerItem.type==='constant'">
                        <input type="text"
                               inputmode="decimal"
                               class="form-control"
                               :value="importerItem.value"
                               @input="setConstantValue(importerItem, $event.target.value)"
                               :placeholder="$t('balance_importer.constant_value')">
                    </div>
                    <div v-if="importerItem.type==='action'">
                        <v-select :clearable="false"
                                  :reduce="item => item.value"
                                  :searchable="false"
                                  :getOptionLabel="(item) => $t(item.label)"
                                  v-model="importerItem.actionName"
                                  :options="actionNames">
                        </v-select>
                    </div>
                </td>

                <td>
                    <div v-if="importerItem.type==='column'">
                        <label>
                            <input type="checkbox" v-model="importerItem.isAbs">
                            Wartość bezwzględna
                        </label>
                    </div>
                </td>
                <td>
                    <div v-if="importerItem.type==='column'">
                        {{ getSampleValue(importerItem) }}
                    </div>
                    <div v-if="importerItem.type==='constant'">
                        {{ getOperandValue(importerItem) }}
                    </div>
                    <div v-if="importerItem.type==='action'">
                        {{ $t(actionNames.find(action => action.value === importerItem.actionName)?.label ?? '') }}
                    </div>
                </td>
            </tr>
            </tbody>
            <tfoot v-if="vat.items.length>0">
            <tr>
                <td>{{ $t('balance_importer.base') }}</td>
                <td></td>
                <td>
                    {{ evaluateResult('vats', index) }}
                    <span v-if="hasDivisionByZero('vats', index)" class="d-block text-warning">
                        {{ $t('balance_importer.division_by_zero') }}
                    </span>
                    <span v-if="hasUnknownAction('vats', index)" class="d-block text-warning">
                        {{ $t('balance_importer.unknown_action') }}
                    </span>
                </td>
            </tr>
            <tr>
                <td>{{ $t('balance_importer.vat_value') }}</td>
                <td></td>
                <td>
                    {{ evaluateVat(index) }}
                </td>
            </tr>
            </tfoot>
        </table>
        <button v-if="canShowOperandButton('vats',index)" type="button" class="btn btn-primary"
                @click="addItem('vats','column',index)">
            {{ $t('balance_importer.add_column') }}
        </button>
        <button v-if="canShowOperandButton('vats',index)" type="button" class="btn btn-primary"
                @click="addItem('vats','constant',index)">
            {{ $t('balance_importer.add_constant') }}
        </button>
        <button v-if="canShowActionButton('vats',index)" type="button" class="btn btn-primary"
                @click="addItem('vats','action',index)">
            {{ $t('balance_importer.add_action') }}
        </button>
        <button type="button" class="btn btn-outline-danger"
                v-if="importer['vats'][index].items.length>0"
                @click="removeItem('vats',index)">
            {{ $t('balance_importer.remove_last_record') }}
        </button>
    </div>
    <button type="button" class="btn btn-secondary"
            @click="addVat()">
        {{ $t('balance_importer.add_vat') }}
    </button>
    <button type="button" class="btn btn-outline-danger"
            v-if="importer['vats'].length>0"
            @click="removeItem('vats')">
        {{ $t('balance_importer.remove_last_vat') }}
    </button>


    <div class="my-4" v-if="importer">
        <h3>{{ $t('balance_importer.driver_id') }}</h3>
        <table class="table table-striped">
            <thead>
            <tr>
                <th class="w-50">{{ $t('balance_importer.fields') }}</th>
                <th class="w-50">{{ $t('balance_importer.sample_id') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="importerItem in importer['externalId']">
                <td>
                    <v-select :clearable="false"

                              :getOptionLabel="getOptionLabel"
                              v-model="importerItem.columnName"
                              :options="columnNames">

                    </v-select>

                </td>
                <td>
                    <div v-if="importerItem.type==='column'">
                        {{ sampleData[importerItem.columnName] }}
                    </div>
                </td>
            </tr>
            </tbody>
            <tfoot>
            <tr>
                <td></td>
                <td>
                    {{ evaluateString('externalId', ':') }}
                </td>
            </tr>
            </tfoot>
        </table>
        <button type="button" class="btn btn-primary"
                @click="addItem('externalId','column')">
            {{ $t('balance_importer.add_column') }}
        </button>
        <button type="button" class="btn btn-outline-danger"
                v-if="importer['externalId'].length>0"
                @click="removeItem('externalId')">
            {{ $t('balance_importer.remove_last_record') }}
        </button>


    </div>
    <div class="mb-4" v-if="importer">
        <h3>{{ $t('balance_importer.additional_data') }}</h3>
        <table class="table table-striped">
            <thead>
            <tr>
                <th class="w-50">{{ $t('balance_importer.fields') }}</th>
                <th class="w-50">{{ $t('balance_importer.sample_data') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="importerItem in importer['additionalData']">
                <td>
                    <v-select :clearable="false"

                              :getOptionLabel="getOptionLabel"
                              v-model="importerItem.columnName"
                              :options="columnNames">

                    </v-select>

                </td>
                <td>
                    <div v-if="importerItem.type==='column'">
                        {{ sampleData[importerItem.columnName] }}
                    </div>
                </td>
            </tr>
            </tbody>
            <tfoot>
            <tr>
                <td></td>
                <td>
                    {{ evaluateString('additionalData', ' ') }}
                </td>
            </tr>
            </tfoot>
        </table>
        <button type="button" class="btn btn-primary"
                @click="addItem('additionalData','column')">
            {{ $t('balance_importer.add_column') }}
        </button>
        <button type="button" class="btn btn-outline-danger"
                v-if="importer['additionalData'].length>0"
                @click="removeItem('additionalData')">
            {{ $t('balance_importer.remove_last_record') }}
        </button>
    </div>
    <div class="mb-4" v-if="importer && importer.filter">
        <h3><label><input type="checkbox" v-model="importer.filter.active"> {{ $t('balance_importer.data_filtering') }}</label></h3>
        <table class="table table-striped" v-if="importer.filter.active">
            <thead>
            <tr>
                <th>{{ $t('balance_importer.field') }}</th>
                <th>{{ $t('balance_importer.condition') }}</th>
                <th>{{ $t('balance_importer.value') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr>
                <td>
                    <v-select :clearable="false"
                              :getOptionLabel="getOptionLabel"
                              v-model="importer.filter.field"
                              :options="columnNames">

                    </v-select>
                </td>
                <td>
                    <v-select :clearable="false"
                              v-model="importer.filter.condition"
                              :options="['=', '!=', '>', '<', '>=', '<=']">

                    </v-select>
                </td>
                <td>
                    <input type="text" v-model="importer.filter.value" class="form-control">
                </td>
            </tr>

            </tbody>

        </table>

    </div>

    <input type="hidden" name="importer" :value="getImporterJson()">
</template>

<style scoped>

</style>
